// DEPRECATED — kept as a shim so legacy import sites don't break the build.
// New code should import from `src/lib/analytics/events.js` directly:
//   import track from '../lib/analytics/events';
//   track.welcomeContinueClicked();
//
// The hooks below funnel into the same `track.*` helpers under the hood.

import { useEffect, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import track from "../lib/analytics/events";
import { getPageName } from "../lib/analytics/pageMap";
import analyticsService from "../lib/AnalyticsService";

// Page-view tracking driven by the pageMap. Unmapped paths fire nw_page_unknown
// with the raw pathname as a property so we notice gaps.
export const usePageTracking = () => {
  const location = useLocation();
  const previousLocation = useRef(null);

  useEffect(() => {
    const currentPath = location.pathname;
    if (previousLocation.current === currentPath) return;

    const allParams = Object.fromEntries(new URLSearchParams(location.search));
    const nonUtmParams = Object.fromEntries(
      Object.entries(allParams).filter(
        ([k]) => !k.toLowerCase().startsWith("utm_")
      )
    );

    const pageName = getPageName(currentPath);
    if (pageName) {
      track.pageViewed(pageName, {
        pathname: currentPath,
        previous_pathname: previousLocation.current,
        query_params: nonUtmParams,
      });
    } else {
      track.pageViewedUnknown(currentPath);
    }

    previousLocation.current = currentPath;
  }, [location.pathname, location.search]);
};

// Legacy interaction shim — forwards to track.custom so old call sites don't crash.
export const useInteractionTracking = () => {
  const trackClick = useCallback((element, additionalProps = {}) => {
    track.custom("nw_legacy_click", {
      element_type: element?.tagName?.toLowerCase(),
      element_id: element?.id,
      ...additionalProps,
    });
  }, []);

  const trackFormSubmit = useCallback((formName, formData = {}, success = true) => {
    track.custom("nw_legacy_form_submit", {
      form_name: formName,
      success,
      field_count: Object.keys(formData).length,
    });
  }, []);

  const trackButtonClick = useCallback((buttonName, additionalProps = {}) => {
    track.custom("nw_legacy_button_click", {
      button_name: buttonName,
      ...additionalProps,
    });
  }, []);

  return { trackClick, trackFormSubmit, trackButtonClick };
};

export const useErrorTracking = () => {
  const trackError = useCallback((errorName, error, context = {}) => {
    analyticsService.trackError(errorName, error, context);
  }, []);

  const trackAPIError = useCallback((endpoint, error, context = {}) => {
    analyticsService.trackError("api_error", error, { endpoint, ...context });
  }, []);

  const trackNetworkError = useCallback((error, context = {}) => {
    analyticsService.trackError("network_error", error, context);
  }, []);

  return { trackError, trackAPIError, trackNetworkError };
};

// Legacy auth shim — prefer track.emailLinkRequested / googleSigninSucceeded etc.
export const useAuthTracking = () => {
  const trackLogin = useCallback((method, success = true, error = null) => {
    track.custom("nw_legacy_auth", {
      auth_event: "login",
      auth_method: method,
      success,
      error_message: error?.message,
    });
  }, []);

  const trackLogout = useCallback(() => {
    track.reset();
  }, []);

  const trackSignup = useCallback((method, success = true, error = null) => {
    track.custom("nw_legacy_auth", {
      auth_event: "signup",
      auth_method: method,
      success,
      error_message: error?.message,
    });
  }, []);

  const trackVerification = useCallback(
    (method, success = true, error = null) => {
      track.custom("nw_legacy_auth", {
        auth_event: "verification",
        auth_method: method,
        success,
        error_message: error?.message,
      });
    },
    []
  );

  return { trackLogin, trackLogout, trackSignup, trackVerification };
};

// Legacy file shim — prefer track.documentUploadStarted / Succeeded / Failed.
export const useFileTracking = () => {
  const trackFileUpload = useCallback(
    (fileName, fileSize, fileType, success = true, error = null) => {
      track.custom("nw_legacy_file_upload", {
        file_name: fileName,
        file_size_bytes: fileSize,
        file_type: fileType,
        success,
        error_message: error?.message,
      });
    },
    []
  );

  const trackDocumentView = useCallback((documentName, documentType) => {
    track.custom("nw_legacy_document_view", {
      document_name: documentName,
      document_type: documentType,
    });
  }, []);

  return { trackFileUpload, trackDocumentView };
};

// Legacy survey shim — prefer track.surveyStarted / Submitted / etc.
export const useSurveyTracking = () => {
  const trackSurveyStart = useCallback((surveyName) => {
    track.surveyStarted({ survey_name: surveyName });
  }, []);

  const trackSurveyQuestion = useCallback(
    (surveyName, questionNumber, questionText, answer) => {
      track.surveyQuestionAnswered({
        survey_name: surveyName,
        question_number: questionNumber,
        question_text: questionText?.slice(0, 100),
        has_answer: answer !== null && answer !== undefined && answer !== "",
      });
    },
    []
  );

  const trackSurveyComplete = useCallback(
    (surveyName, totalQuestions, completionTime) => {
      track.surveySubmitted({
        survey_name: surveyName,
        total_questions: totalQuestions,
        completion_time_ms: completionTime,
      });
    },
    []
  );

  const trackSurveyAbandonment = useCallback(
    (surveyName, questionNumber, totalQuestions) => {
      track.custom("nw_survey_abandoned", {
        survey_name: surveyName,
        abandoned_at_question: questionNumber,
        total_questions: totalQuestions,
        completion_percentage: (questionNumber / totalQuestions) * 100,
      });
    },
    []
  );

  return {
    trackSurveyStart,
    trackSurveyQuestion,
    trackSurveyComplete,
    trackSurveyAbandonment,
  };
};

// Legacy integration shim
export const useIntegrationTracking = () => {
  const trackIntegrationStart = useCallback((integrationType, integrationName) => {
    track.custom("nw_integration_started", {
      integration_type: integrationType,
      integration_name: integrationName,
    });
  }, []);

  const trackIntegrationSuccess = useCallback(
    (integrationType, integrationName, data = {}) => {
      track.custom("nw_integration_succeeded", {
        integration_type: integrationType,
        integration_name: integrationName,
        ...data,
      });
    },
    []
  );

  const trackIntegrationError = useCallback(
    (integrationType, integrationName, error) => {
      analyticsService.trackError("integration_error", error, {
        integration_type: integrationType,
        integration_name: integrationName,
      });
    },
    []
  );

  const trackIntegrationSkip = useCallback(
    (integrationType, integrationName, reason = "user_choice") => {
      track.custom("nw_integration_skipped", {
        integration_type: integrationType,
        integration_name: integrationName,
        skip_reason: reason,
      });
    },
    []
  );

  return {
    trackIntegrationStart,
    trackIntegrationSuccess,
    trackIntegrationError,
    trackIntegrationSkip,
  };
};
